import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { DNABox, DNACheckbox, DNAIcon, DNAText } from '@alucio/lux-ui';
import { DisplayMode } from 'src/components/DNA/hooks/useDisplayMode'
/** Possible future refactor of Input to lux-ui export per Jose suggestion  */
import { Input } from '@ui-kitten/components';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { useCanPerformSearch } from 'src/state/redux/selector/documentSearch/documentSearch';
import debounce from 'lodash/debounce';
import { DocumentORM } from 'src/types/types';
import { Options } from '../../useMyContentPanelSharedResources';
import DNAActionBar, { DNAActionBarItemProps } from 'src/components/DNA/ActionBar/DNAActionBar';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import InputComponent from 'src/components/Publishers/InputComponent';
import { useAppSettings } from 'src/state/context/AppSettings';

const styles = StyleSheet.create({
  searchInput: {
    borderRadius: 100,
  },
  searchTextInput: {
    borderRadius: 0,
  },
  hiddenClearIcon: {
    opacity: 0,
  },
});

interface HeaderPresenterModeProps {
  localSearchText: string,
  onSelectTab: (tab: Options) => void,
  selectedTab: Options,
  displayMode: DisplayMode,
  setLocalSearchText: (text: string) => void,
  setSearchText: (text: string) => void,
  fullTextSearchEnabled: boolean,
  setFullTextSearchEnabled: (enabled: boolean) => void,
  sessionId?: string,
  selectedDocument?: DocumentORM
}

const actionBarItemDefaults: DNAActionBarItemProps = {
  colorMode: 'dark',
}

const HeaderPresenterMode: React.FC<HeaderPresenterModeProps> = (props) => {
  const {
    localSearchText,
    onSelectTab,
    selectedTab,
    setLocalSearchText,
    setSearchText,
    fullTextSearchEnabled,
    setFullTextSearchEnabled,
    sessionId,
  } = props;

  const canPerformSearch = useCanPerformSearch();
  const { isOnline } = useAppSettings();
  // Used to return to that tab if user clears the search input
  const tabSelectedBeforeSearch = useRef<Options>(Options.FOLDERS)
  const inputRef = useRef<Input>(null)

  const handleTabSelect = useCallback((tab: Options) => () => {
    setLocalSearchText('');
    onSelectTab(tab);
  }, [])

  const handleTrackAction = useCallback(debounce((text: string): void => {
    analytics?.track('VIRTUAL_CONTENT_SEARCH', {
      action: 'CONTENT_SEARCH',
      category: 'VIRTUAL',
      searchText: text,
      sessionId,
    });
  }, 1000), [])

  // IF WE DON'T MEMOIZE, ANOTHER INSTANCE OF HANDLESEARCH WILL BE CREATED UPON EVERY RENDER
  // THEREFORE, THE DEBOUNCE EFFECT WE NEED WON'T HAVE ANY EFFECT`
  const MemoizedHandleTrackAction = useCallback(handleTrackAction, [sessionId]);

  const debouncedSetSearchText = useMemo(
    () => debounce((text: string) => {
      setSearchText(text);
    }, 1000),
    [setSearchText],
  )

  useEffect(() => () => {
    debouncedSetSearchText.cancel();
  }, [debouncedSetSearchText])

  useEffect(() => {
    if (!isOnline && fullTextSearchEnabled) {
      setFullTextSearchEnabled(false);
    }
  }, [fullTextSearchEnabled, isOnline, setFullTextSearchEnabled])

  const canSearchText = useCallback(
    (text: string, shouldUseFullTextSearch = fullTextSearchEnabled) =>
      shouldUseFullTextSearch ? canPerformSearch(text) : !!text.trim(),
    [canPerformSearch, fullTextSearchEnabled],
  )

  const syncSelectedSearchTab = useCallback(
    (text: string, shouldUseFullTextSearch = fullTextSearchEnabled) => {
      const canTextBeSearchable = canSearchText(text, shouldUseFullTextSearch);

      if (canTextBeSearchable && selectedTab && selectedTab !== Options.SEARCH_RESULTS) {
        tabSelectedBeforeSearch.current = selectedTab;
        onSelectTab(Options.SEARCH_RESULTS);
      } else if (!canTextBeSearchable && selectedTab === Options.SEARCH_RESULTS) {
        onSelectTab(tabSelectedBeforeSearch.current);
      }
    },
    [canSearchText, fullTextSearchEnabled, onSelectTab, selectedTab],
  )

  const updateSearchText = useCallback(
    (text: string, shouldUseFullTextSearch = fullTextSearchEnabled) => {
      if (!text.trim()) {
        debouncedSetSearchText.cancel();
        setSearchText('');
        return;
      }

      if (shouldUseFullTextSearch) {
        debouncedSetSearchText(text);
      } else {
        debouncedSetSearchText.cancel();
        setSearchText(text);
      }
    },
    [debouncedSetSearchText, fullTextSearchEnabled, setSearchText],
  )

  const onSearchTextChange = (text: string) => {
    setLocalSearchText(text);

    updateSearchText(text);
    text && MemoizedHandleTrackAction(text);
    syncSelectedSearchTab(text);
  }

  const onFullTextSearchChange = (checked: boolean) => {
    setFullTextSearchEnabled(checked);
    updateSearchText(localSearchText, checked);
    syncSelectedSearchTab(localSearchText, checked);
  }

  const handleClearSearch = useCallback(() => {
    onSearchTextChange('');
    inputRef.current?.focus();
  }, [onSearchTextChange])

  const renderClearSearchIcon = useCallback(() => {
    return (
      <TouchableOpacity
        disabled={!localSearchText}
        onPress={handleClearSearch}
        style={!localSearchText && styles.hiddenClearIcon}
      >
        <DNAIcon.Styled
          name="x"
          appearance="ghost"
          status="subtle"
        />
      </TouchableOpacity>
    );
  }, [handleClearSearch, localSearchText])

  const getTabProps = useCallback((tabID: Options): DNAActionBarItemProps => ({
    appearance: selectedTab === tabID ? 'filled' : 'ghost',
    status: 'secondary',
    onPress: handleTabSelect(tabID),
    ...actionBarItemDefaults,
  }), [selectedTab, handleTabSelect])

  return (
    <DNABox alignY="center" style={{ paddingHorizontal: 24, justifyContent: 'space-between' }}>
      <DNABox alignY="center" style={{ paddingTop: 24, paddingBottom:16 }} appearance="col" fill spacing="md">
        <DNABox fill alignY="center" spacing="md" childFill={0}>
          <DNABox appearance="col" fill alignX="stretch">
            <InputComponent
              ref={inputRef}
              hideLabel
              removeMarginPadding
              onChangeText={onSearchTextChange}
              getRightIconFunction={renderClearSearchIcon}
              size="md"
              inputStyle={styles.searchInput}
              textStyle={styles.searchTextInput}
              value={localSearchText}
              title={fullTextSearchEnabled ? 'Search files' : 'Search by file name'}
              testID="meeting-presentations-search-input"
            />
          </DNABox>
          {isOnline && (
            <DNABox alignY="center" spacing="sm">
              <DNACheckbox
                checked={fullTextSearchEnabled}
                onChange={onFullTextSearchChange}
                testID="meeting-full-text-search-checkbox"
              />
              <DNAText b2 style={{ color: colors['color-text-dark-primary'] }}>
                Full text search
              </DNAText>
            </DNABox>
          )}
        </DNABox>
        <DNABox>

          <DNAActionBar
            fill
            spacing="md"
            itemDefaults={actionBarItemDefaults}
          >
            <DNAActionBar.Item
              {...getTabProps(Options.FOLDERS)}
              key={Options.FOLDERS}
              testID="meeting-my-folders-tab"
            >
              {Options.FOLDERS}
            </DNAActionBar.Item>
            <DNAActionBar.Item
              {...getTabProps(Options.SHARED_WITH_ME)}
              key={Options.SHARED_WITH_ME}
              testID="meeting-shared-with-me-tab"
            >
              {Options.SHARED_WITH_ME}
            </DNAActionBar.Item>
            <DNAActionBar.Item
              {...getTabProps(Options.FAVORITE_FOLDERS)}
              key={Options.FAVORITE_FOLDERS}
              testID="meeting-favorite-folders-tab"
            >
              {Options.FAVORITE_FOLDERS}
            </DNAActionBar.Item>
            <DNAActionBar.Item
              {...getTabProps(Options.BOOKMARKS)}
              key={Options.BOOKMARKS}
              testID="meeting-bookmarks-tab"
            >
              {Options.BOOKMARKS}
            </DNAActionBar.Item>
            <DNAActionBar.Item
              {...getTabProps(Options.LIBRARY)}
              key={Options.LIBRARY}
              testID="meeting-library-tab"
            >
              {Options.LIBRARY}
            </DNAActionBar.Item>
          </DNAActionBar>
        </DNABox>
      </DNABox>
    </DNABox>
  );
}

HeaderPresenterMode.displayName = 'ContentPanelHeader';

export default HeaderPresenterMode;
